import {
	CognitoUserPool,
	CognitoUser,
	CognitoUserSession,
	CognitoAccessToken,
	CognitoIdToken,
	CognitoRefreshToken,
} from 'amazon-cognito-identity-js';
import * as AWS_SDK from 'aws-sdk';
import emailMask from 'email-mask';
import {
	AuthFlowType,
	CognitoIdentityProviderClient,
	LimitExceededException,
	NotAuthorizedException,
	PasswordResetRequiredException,
	UserNotConfirmedException,
	UserNotFoundException,
} from '@aws-sdk/client-cognito-identity-provider';
import { Injectable, Injector } from '@angular/core';
import { Observable, Subject, Subscription, tap } from 'rxjs';
import { ENV, Environment } from '../../config/environment';
import { AppUtil } from '../../utils/app.util';
import { JwtPayload, jwtDecode } from 'jwt-decode';
import { Config } from '../../config/config';
import { DataCarrierService } from './data-carrier.service';
import { UTMParameters } from '../../beans/auth/utm-parameters.bean';
import { UtmParams } from '../../modules/scheduling/types.scheduling';
import { JwtHelper } from './jwt-helper.service';
import { APP_USER_PERMISSIONS, PERMISSON_ALLOWED_KEY } from 'src/app/constants/app-user-permissions.constants';
import { NgxPermissionsService } from 'ngx-permissions';
import { MfaService } from './mfa.service';
import { SpinnerComponent } from 'src/app/modules/shared/spinner/spinner.component';
import { AUTH_COMPONENTS } from 'src/app/constants/scheduling.constants';
import { ModalService } from './modal.service';
import { ProfileService } from './profile.service';
import { MfaSetupState, MfaUiCommands, SsoType } from 'src/app/modules/shared/types';
import { Router } from '@angular/router';
import { signIn, fetchAuthSession, confirmSignIn, AuthSession, signOut, decodeJWT, JWT } from 'aws-amplify/auth';
import { Amplify } from 'aws-amplify';
import { ClientMetadata, ConfirmSignInOutput } from '@aws-amplify/auth/dist/esm/providers/cognito/types';
import { CredentialsOptions } from 'aws-sdk/lib/credentials';
import { SsoIdpInitiatedComponent } from 'src/app/modules/sso/sso-idp-initiated/sso-idp-initiated.component';
import { LoginsMap } from 'aws-sdk/clients/cognitoidentity';
import { SsoRedirectionComponent } from 'src/app/modules/sso/sso-redirection/sso-redirection.component';

type ConfirmSignInOutputWithError = ConfirmSignInOutput & { error?: any };
/** AWS Object */
declare let AWS: any;
/** Cognito pool data object */
let PoolData = {
	ClientId: '',
	UserPoolId: '',
	IdentityPoolId: '',
};
/** hold user pool object */
let userPool: CognitoUserPool;

/**
 * Cognito related authentication services
 */
@Injectable({
	providedIn: 'root',
})
export class AuthService {
	env: ENV;
	/** Observable returning the status cognito operations */
	result: Subject<any>;
	signUpResult: Subject<any>;
	response: any;
	cognitoUser: CognitoUser;
	loginSessionTimeout: NodeJS.Timeout | string | number | undefined;
	loginFlowSubscriptions: Subscription = new Subscription();
	mfaFlowSubscriptions: Subscription = new Subscription();
	mfaCodePromptSubscription: Subscription = new Subscription();
	private currentUserSessionPromise: Promise<AuthSession>;
	private currentUserSession: AuthSession;
	private cognitoClient: CognitoIdentityProviderClient;
	/**
	 * Auth Service constructor
	 * @param environment - Environment Config
	 * @param config - URL Config
	 * @param dataCarrier - Data Carrier Service
	 * @param router - Angular Router
	 */
	constructor(
		private readonly environment: Environment,
		private readonly config: Config,
		private readonly dataCarrier: DataCarrierService,
		private readonly jwtHelper: JwtHelper,
		private readonly permissionService: NgxPermissionsService,
		private router: Router,
		private readonly mfaService: MfaService,
		private readonly spinner: SpinnerComponent,
		private readonly modalService: ModalService,
		private readonly injector: Injector
	) {
		require('aws-sdk/lib/token.js');
		this.env = this.environment.getEnvironment();
		/** constricting cognito pool data using cognito credentials */
		PoolData = {
			ClientId: this.env.CLIENT_ID,
			UserPoolId: this.env.USER_POOL_ID,
			IdentityPoolId: this.env.IDENTITY_POOL_ID,
		};
		userPool = new CognitoUserPool(PoolData);
		this.result = new Subject<any>();
		this.signUpResult = new Subject<any>();
		this.configureAmplify();
	}

	configureAmplify(): void {
		Amplify.configure({
			...Amplify.getConfig(),
			auth: {
				aws_region: this.env.AWS_REGION,
				user_pool_client_id: PoolData.ClientId,
				user_pool_id: PoolData.UserPoolId,
				identity_pool_id: PoolData.IdentityPoolId,
				unauthenticated_identities_enabled: true,
			},
			Auth: {
				Cognito: {
					userPoolId: PoolData.UserPoolId,
					userPoolClientId: PoolData.ClientId,
					identityPoolId: PoolData.IdentityPoolId,
					allowGuestAccess: true,
					loginWith: {
						email: true,
					},
				},
			},
			Geo: {
				LocationService: {
					region: this.env.AWS_REGION,
					searchIndices: {
						items: [this.env.AWS_LOCATION_SERVICE_PLACE_INDEX_NAME],
						default: this.env.AWS_LOCATION_SERVICE_PLACE_INDEX_NAME,
					},
				},
			},
		});
		this.updateAwsCredentials();
	}

	async configureCognitoClient() {
		try {
			const env: any = this.environment.getEnvironment();
			this.cognitoClient = new CognitoIdentityProviderClient({
				region: env.AWS_REGION,
				credentials: (await this.getCurrentAmplifyUserAuthSession()).credentials,
			});
			return this.cognitoClient;
		} catch (error) {
			console.error('Error configuring Cognito client:', error);
			throw new Error('Failed to configure Cognito client');
		}
	}

	/**
	 * Getter method for IDToken
	 */
	getToken() {
		let jwtToken = null;
		if (sessionStorage.getItem(this.config.TOKEN_NAME)) {
			const authToken = JSON.parse(sessionStorage.getItem(this.config.TOKEN_NAME));
			jwtToken = authToken.idToken.jwtToken;
		}
		return jwtToken;
	}

	loadAppLevelPermissions() {
		const applicationPermissionsForLoggedInUser = this.getAppPermissionsForLoggedInUser();
		if (applicationPermissionsForLoggedInUser) {
			applicationPermissionsForLoggedInUser.forEach(permission => {
				this.permissionService.addPermission(permission);
			});
		}
		// console.log('permissions available are ', this.permissionService.getPermissions());
	}

	getAppPermissionsForLoggedInUser() {
		const decodedToken = this.jwtHelper.getDecodedToken();
		if (decodedToken && decodedToken[PERMISSON_ALLOWED_KEY]) {
			const permissionsStringArray = decodedToken ? decodedToken[PERMISSON_ALLOWED_KEY].split(',') : null;
			if (permissionsStringArray) {
				// parse string into enumerables thus only allowing permissions which are mutually decided both on FE and BE
				const permissions = permissionsStringArray
					.map((permission: string) => {
						if (permission in APP_USER_PERMISSIONS) {
							return APP_USER_PERMISSIONS[permission];
						}
						return null;
					})
					.filter(p => p);
				return permissions;
			}
		}
		return null;
	}

	async hasPermission(permission: string): Promise<boolean> {
		const permissions = this.permissionService.getPermissions();
		return !!permissions[permission];
	}

	/**
	 * Getter method for Token expiry date
	 * @param token IDToken
	 */
	getTokenExpirationDate(token: string): Date {
		const decoded = jwtDecode<JwtPayload>(token);
		if (decoded.exp === undefined) {
			return null;
		}
		const date = new Date(decoded.exp);
		return date;
	}

	/**
	 * checking whether the given token expired or not
	 * by checking expiry timestamp of token
	 * @param token - IDToken
	 * return true or false
	 */
	isTokenExpired(token?: string): boolean {
		if (!token) {
			token = this.getToken();
		}
		if (!token) {
			return true;
		}
		const date = this.getTokenExpirationDate(token);
		// Fail-closed: a token with no/unreadable exp claim is treated as expired.
		// Previously this checked `=== undefined` but `getTokenExpirationDate` returns null,
		// which fell through and crashed on date.valueOf(). See DE-5734 test coverage work.
		if (date === undefined || date === null) {
			return true;
		}
		// Rounding off current timestamp to 10 digits
		const currentTimestamp = Math.floor(new Date().valueOf() / 1000);
		return !(date.valueOf() > currentTimestamp);
	}

	/**
	 * Cognito Sign up
	 * @param object - contain user related information like email and password
	 * return the response using Observable
	 */
	signUpUser(object) {
		this.signUpResult = new Subject<any>();
		userPool.signUp(AppUtil.getFormattedEmail(object.email), object.password, null, null, (err, res) => {
			if (err) {
				console.error(`[${AuthService.name}][${this.signUpUser.name}]`, 'Error during user signup', object.email, err);
				const error = { success: false, object: err };
				this.signUpResult.error(error);
			} else {
				this.signUpResult.next({ success: true, object: '' });
			}
		});
	}

	clearStorageAndCredentials() {
		/**
		 * clear local storage, session storage and  AWS Credentials
		 * before new session begins
		 */
		// Getting utm params which is set before login, and setting back to local storage
		const utm = localStorage.getItem('utm');
		const sidebarCollapsed = localStorage.getItem('sidebarCollapsed');
		const forgotPassToaster = sessionStorage.getItem('forgotPassToaster');
		const createAccountToaster = sessionStorage.getItem('createAccountToaster');
		const userType = sessionStorage.getItem('userType');
		const globalMfaStatus = this.mfaService.ss_getGlobalMfaStatus();
		const mfaActivatedAt = this.mfaService.ss_getMfaActivatedAt();

		if (window[this.config.userLoginSession.sessionCheckIntervalVarName]) {
			clearInterval(window[this.config.userLoginSession.sessionCheckIntervalVarName]);
		}
		try {
			signOut().catch(() => {});
		} catch (error) {}
		localStorage.clear();
		sessionStorage.clear();
		this.currentUserSession = undefined;
		this.currentUserSessionPromise = undefined;

		// Setting back the params in local storage
		localStorage.setItem('utm', utm);
		if (sidebarCollapsed) {
			localStorage.setItem('sidebarCollapsed', sidebarCollapsed);
		}
		sessionStorage.setItem('forgotPassToaster', forgotPassToaster);
		sessionStorage.setItem('createAccountToaster', createAccountToaster);
		if (userType) {
			sessionStorage.setItem('userType', userType);
		}
		this.mfaService.ss_setGlobalMfaStatus(globalMfaStatus);
		this.mfaService.ss_setMfaActivatedAt(mfaActivatedAt);

		if (AWS_SDK.config.credentials) {
			AWS.config.credentials.clearCachedId();
		}
	}

	private handleLoginError(error: any, email: string) {
		// Forcing reset password, when email is changed on epms side
		console.info('Auth.service.ts -> handleLoginError(error):', error);
		this.mfaService.toggleDisplayMfaInputScreen(MfaUiCommands.HIDE);

		if (error instanceof NotAuthorizedException) {
			console.log('instanceof NotAuthorizedException');
			this.result.next({
				error: {
					message: 'Invalid username or password',
					code: '400',
					name: 'Invalid username or password',
				},
			});
		} else if (error instanceof UserNotFoundException) {
			console.log('instanceof UserNotFoundException');
			this.result.next({
				error: {
					message: 'User not found',
					code: '404',
					name: 'UserNotFoundException',
				},
			});
		} else if (error instanceof UserNotConfirmedException) {
			console.log('instanceof UserNotConfirmedException');
			this.result.next({
				error: {
					message: 'User is not confirmed',
					code: '403',
					name: 'UserNotConfirmedException',
				},
			});
		} else if (error instanceof PasswordResetRequiredException) {
			console.log('instanceof PasswordResetRequiredException');
			this.router.navigate(['/reset/password'], {
				queryParams: { email: btoa(email), resetPassword: true },
			});
			this.result.next({
				error: {
					message: 'Password reset is required',
					code: '401',
					name: 'PasswordResetRequiredException',
				},
			});
		} else if (error instanceof LimitExceededException) {
			console.log('instanceof LimitExceededException');
			this.result.next({
				error: {
					message: 'Maximum attempt exceeded',
					code: '400',
					name: 'LimitExceededException',
				},
			});
		} else {
			console.log('Unknown Exception in [handleLoginError]');
			this.result.next({ error });
		}
		this.spinner.stopLoader();
	}

	private clearLoginFlowSubscriptions() {
		this.loginFlowSubscriptions.unsubscribe();
		this.mfaFlowSubscriptions.unsubscribe();
		this.clearMFAFlowSubscriptions();
	}

	private clearMFAFlowSubscriptions() {
		this.mfaCodePromptSubscription.unsubscribe();
		this.mfaFlowSubscriptions.unsubscribe();
	}

	/**
	 * Cognito Login
	 * @param email - user email Id
	 * @param password - user password
	 */
	async login(email: string, password: string, authMetadata: string = AUTH_COMPONENTS.LOGIN_COMPONENT) {
		if (this.loginSessionTimeout) {
			console.info('LOGIN ALREADY IN PROGRESS');
			return;
		}

		// Clear storage and AWS credentials
		this.clearStorageAndCredentials();

		this.loginFlowSubscriptions = new Subscription();

		try {
			// await this.configureCognitoClient();
			const clientMetadata: ClientMetadata = {
				loginSource: authMetadata,
			};

			this.loginFlowSubscriptions.add(
				// MFA Input Form Closing
				this.mfaService.displayMfaInputScreen.subscribe(showMfaInputScreen => {
					console.info('displayMfaInputScreen.showMfaInputScreen?: ', showMfaInputScreen);
					if (showMfaInputScreen === MfaUiCommands.HIDE && !this.jwtHelper.getDecodedToken()) {
						this.clearLoginSessionTimeout();
						this.clearLoginFlowSubscriptions();
					}
				})
			);
			this.loginFlowSubscriptions.add(
				// Send/Resend MFA Code
				this.mfaService.resendMfaCodeObserver.subscribe(async resendMfaCode => {
					console.info('resendMfaCodeObserver.resendMfaCode?: ', resendMfaCode);
					if (resendMfaCode) {
						await this.loginWithMfa(email, password, PoolData.ClientId, clientMetadata);
					}
				})
			);

			// Start Login Process
			this.mfaService.resendMfaCode(true);
		} catch (error) {
			console.error('Login error:', error);
			this.clearLoginSessionTimeout();
			this.clearLoginFlowSubscriptions();
			// this.handleLoginError(error, email);
		}
	}

	private async loginWithMfa(email: string, password: string, clientId: string, clientMetadata: ClientMetadata) {
		try {
			this.clearLoginSessionTimeout();
			this.cognitoUser = new CognitoUser({
				Username: email,
				Pool: userPool,
			});

			// https://docs.amplify.aws/angular/build-a-backend/auth/connect-your-frontend/sign-in/
			const singInRes = await signIn({
				username: email,
				password,
				options: { authFlowType: AuthFlowType.USER_PASSWORD_AUTH, clientMetadata },
			});

			if (singInRes.nextStep.signInStep === 'DONE') {
				await this.cognitoSessionUpdate();
				this.clearLoginSessionTimeout();
				this.clearLoginFlowSubscriptions();
			} else if (
				singInRes.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE' ||
				singInRes.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE' ||
				singInRes.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE'
			) {
				this.clearMFAFlowSubscriptions();
				this.mfaFlowSubscriptions = new Subscription();
				// collect OTP from user
				const subscription = this.handleEmailOtpResponse(email).subscribe(cognitoUserSession => {
					if (!cognitoUserSession) {
						return;
					}
					const idToken = cognitoUserSession?.getIdToken().payload;
					if (idToken) {
						console.info('CognitoUserSession Received - Processing and navigating to dashboard');

						// Handle MFA status safely - might be undefined, boolean, or string
						const mfaEnabledValue = idToken['custom:mfa-enabled'];
						console.info(`idToken['custom:mfa-enabled']:`, mfaEnabledValue, 'Type:', typeof mfaEnabledValue);

						let mfaEnabled = false;
						if (typeof mfaEnabledValue === 'boolean') {
							mfaEnabled = mfaEnabledValue;
						} else if (typeof mfaEnabledValue === 'string') {
							try {
								mfaEnabled = JSON.parse(mfaEnabledValue);
							} catch (e) {
								console.warn('Failed to parse mfa-enabled, treating as string:', mfaEnabledValue);
								mfaEnabled = mfaEnabledValue === 'true';
							}
						}

						this.mfaService.ss_setUserMfaStatus(mfaEnabled);

						const mfaDisabledReason = idToken['custom:mfa-disable-reason'];
						console.info(`idToken['custom:mfa-disable-reason']:`, mfaDisabledReason);

						if (mfaDisabledReason) {
							this.mfaService.ss_setMfaDisabledReason(mfaDisabledReason);
						}
						this.mfaService.fetchGlobalMfaStatus();
						this.mfaService.displayMfaErrorMessage(false, '');

						// Emit result to trigger dashboard navigation
						console.info('Emitting result to navigate to dashboard');
						this.result.next({ data: cognitoUserSession });

						this.clearLoginSessionTimeout();
						this.clearLoginFlowSubscriptions();
					} else {
						console.info('CognitoUserSession NOT Received');
					}
					subscription.unsubscribe();
				});
				this.mfaFlowSubscriptions.add(subscription);
			} else if (singInRes.nextStep.signInStep === 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION') {
				// present nextStep.allowedMFATypes to user
				// collect user selection
				// await confirmSignIn({
				// 	challengeResponse: "EMAIL", // 'EMAIL', 'SMS', or 'TOTP'
				// });
			} else if (singInRes.nextStep.signInStep === 'CONTINUE_SIGN_IN_WITH_MFA_SETUP_SELECTION') {
				// present nextStep.allowedMFATypes to user
				// collect user selection
				// await confirmSignIn({
				// 	challengeResponse: "EMAIL", // 'EMAIL' or 'TOTP'
				// });
			} else if (singInRes.nextStep.signInStep === 'CONTINUE_SIGN_IN_WITH_EMAIL_SETUP') {
				// collect email address from user
				// await confirmSignIn({
				// 	challengeResponse: "hello@mycompany.com",
				// });
			} else if (singInRes.nextStep.signInStep === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP') {
				// present nextStep.totpSetupDetails.getSetupUri() to user
				// collect OTP from user
				// await confirmSignIn({
				// 	challengeResponse: "123456",
				// });
			}
		} catch (error) {
			console.error('Auth Init Command - Error', error);
			this.clearLoginSessionTimeout();
			this.clearLoginFlowSubscriptions();
			this.handleLoginError(error, email);
		}
	}

	private handleEmailOtpResponse(email: string): Observable<CognitoUserSession> {
		console.info('EMAIL-OTP Flow:');
		this.mfaService.toggleDisplayMfaInputScreen(MfaUiCommands.SHOW);
		const maskedEmail = emailMask(email, '*')
			?.replace(/\*+/, '***')
			?.replace(/(@[A-z]).*([A-z])/, '$1***$2');
		setTimeout(() => this.mfaService.setCodeDeliveryDestination(maskedEmail), 0);
		this.spinner.stopLoader(); // Stop the loading spinner

		const subject = new Subject<CognitoUserSession>();

		this.mfaCodePromptSubscription = this.mfaService.mfaCode.subscribe(async mfaCode => {
			mfaCode = mfaCode?.trim();
			if (!mfaCode?.length) {
				return;
			}
			this.spinner.startLoader();
			this.clearLoginSessionTimeout();
			let mfaVerifyResponse: ConfirmSignInOutputWithError;
			try {
				// mfaVerifyResponse = await confirmSignIn({ challengeResponse: mfaCode })
				mfaVerifyResponse = await this.completeMFAChallenge(mfaCode);
				console.info('MFA verification Response:', mfaVerifyResponse);
				if (mfaVerifyResponse?.isSignedIn) {
					console.info('MFA verification success:', mfaVerifyResponse);

					// First, get token by updating session (DON'T emit result yet, DON'T fetch MFA status yet)
					const cognitoUserSession = await this.cognitoSessionUpdate(false, false);

					// Now token is available, check MFA status using authenticated endpoint
					const showSuccessScreen = await this.fetchMfaActivatedStatusAfterTokenSet(true);

					if (showSuccessScreen) {
						// Show success screen and wait for user to dismiss it
						console.info('MFA -> Setting up success screen dismissal subscription');
						const mfaSuccessScreenSubscription = this.mfaService.displayMfaSuccessScreen.subscribe(uiCommand => {
							console.info('MFA -> displayMfaSuccessScreen subscription received:', uiCommand);
							if (uiCommand === MfaUiCommands.HIDE) {
								console.info('MFA -> MFA Success Screen - Dismissed by user');
								mfaSuccessScreenSubscription.unsubscribe();
								this.mfaCodePromptSubscription.unsubscribe();

								// Set MFA data in token before emitting result
								const idToken = cognitoUserSession?.getIdToken().payload;
								if (idToken) {
									console.log(
										'DEBUG: idToken custom:mfa-enabled raw value:',
										idToken['custom:mfa-enabled'],
										'Type:',
										typeof idToken['custom:mfa-enabled']
									);

									// Handle MFA status - it might be a boolean or string
									let mfaEnabled = false;
									const mfaEnabledValue = idToken['custom:mfa-enabled'];
									if (typeof mfaEnabledValue === 'boolean') {
										mfaEnabled = mfaEnabledValue;
									} else if (typeof mfaEnabledValue === 'string') {
										try {
											mfaEnabled = JSON.parse(mfaEnabledValue);
										} catch (e) {
											console.warn('Failed to parse mfa-enabled:', mfaEnabledValue, e);
											mfaEnabled = mfaEnabledValue === 'true';
										}
									}

									this.mfaService.ss_setUserMfaStatus(mfaEnabled);
									const mfaDisabledReason = idToken['custom:mfa-disable-reason'];
									if (mfaDisabledReason) {
										this.mfaService.ss_setMfaDisabledReason(mfaDisabledReason);
									}
									this.mfaService.fetchGlobalMfaStatus();
									this.mfaService.displayMfaErrorMessage(false, '');
								}

								this.spinner.startLoader();
								subject.next(cognitoUserSession);
								subject.complete();
							}
						});
						this.mfaFlowSubscriptions.add(mfaSuccessScreenSubscription);
						console.info('MFA -> Success screen dismissal subscription set up successfully');
					} else {
						// Skip success screen, proceed to app (MFA data already set during token update)
						this.mfaCodePromptSubscription.unsubscribe();

						// Set MFA data in token before emitting result
						const idToken = cognitoUserSession?.getIdToken().payload;
						if (idToken) {
							this.mfaService.ss_setUserMfaStatus(JSON.parse(idToken['custom:mfa-enabled'] || 'false'));
							const mfaDisabledReason = idToken['custom:mfa-disable-reason'];
							if (mfaDisabledReason) {
								this.mfaService.ss_setMfaDisabledReason(mfaDisabledReason);
							}
							this.mfaService.fetchGlobalMfaStatus();
							this.mfaService.displayMfaErrorMessage(false, '');
						}

						subject.next(cognitoUserSession);
						subject.complete();
					}
				} else {
					console.info('Invalid mfaVerifyResponse:', mfaVerifyResponse);
					if (mfaVerifyResponse?.error?.name === 'CodeMismatchException') {
						console.info('CodeMismatchException, allow  retyping');
						// When code mismatch, allow retyping
					} else {
						this.mfaCodePromptSubscription.unsubscribe();
						subject.next(undefined);
						subject.complete();
					}
				}
			} catch (error) {
				console.error('Error handleEmailOtpResponse():', error);
				this.mfaCodePromptSubscription.unsubscribe();
				subject.next(undefined);
				subject.complete();
				this.spinner.stopLoader();
				// mfaCommandCode = 'CONTINUE';
			}
		});
		// this.mfaFlowSubscriptions.add(mfaCodePromptSubscription);
		return subject.asObservable();
	}

	private async cognitoSessionUpdate(fetchMfaStatus: boolean = true, emitResult: boolean = true) {
		// this.result = new Subject<any>();
		const authSession = await this.getCurrentAmplifyUserAuthSession();
		const cognitoUserSession = new CognitoUserSession({
			AccessToken: new CognitoAccessToken({ AccessToken: authSession?.tokens?.accessToken?.toString() }),
			IdToken: new CognitoIdToken({ IdToken: authSession?.tokens?.idToken?.toString() }),
			RefreshToken: new CognitoRefreshToken({ RefreshToken: undefined }),
		});
		sessionStorage.setItem(this.config.TOKEN_NAME, JSON.stringify(cognitoUserSession));
		sessionStorage.setItem('userId', authSession?.tokens?.idToken?.payload?.sub);
		this.cognitoUser.setSignInUserSession(cognitoUserSession);
		this.updateAwsCredentials(authSession);

		// Fetch MFA activated status (only for non-MFA flows, MFA flow handles this separately)
		if (fetchMfaStatus) {
			this.fetchMfaActivatedStatusAfterTokenSet();
		}

		// Only emit result if requested (to allow success screen to show first)
		if (emitResult) {
			this.result.next({ data: cognitoUserSession });
		}
		return cognitoUserSession;
	}

	/**
	 * Fetches MFA activated status after authentication token is set
	 * Extracts email from token and calls ProfileService to get MFA status
	 * Uses Injector to avoid circular dependency
	 * @param checkForSuccessScreen - If true, returns whether to show success screen based on MFA history
	 * @returns Promise<boolean> - If checkForSuccessScreen is true, returns whether to show success screen
	 */
	private fetchMfaActivatedStatusAfterTokenSet(checkForSuccessScreen: boolean = false): Promise<boolean> | void {
		try {
			const decodedToken = this.jwtHelper.getDecodedToken();
			if (!decodedToken || !decodedToken.email) {
				if (checkForSuccessScreen) {
					return Promise.resolve(false);
				}
				return;
			}

			const email = decodedToken.email;

			// Use Injector to get ProfileService lazily to avoid circular dependency
			const profileService = this.injector.get(ProfileService);

			if (checkForSuccessScreen) {
				// Return a promise for success screen decision
				return new Promise<boolean>(resolve => {
					profileService.getMfaActivatedStatus(email).subscribe({
						next: response => {
							console.info('MFA activated status fetched for success screen decision:', response);

							// Update MFA status in session storage
							this.mfaService.ss_setUserMfaStatus(response?.isMfaActive);
							this.mfaService.setUserFirstName(response?.username || 'User');

							// Show success screen if user has NO MFA history (first time)
							const showSuccessScreen = !response?.hasMfaHistory;

							if (showSuccessScreen) {
								console.info('No MFA history - showing success screen');
								this.spinner.stopLoader();
								this.mfaService.setMfaActivatedAt(MfaSetupState.FIRST_TIME);
								this.mfaService.ss_setMfaActivatedAt(MfaSetupState.FIRST_TIME);
								this.mfaService.toggleMfaSuccessScreen(MfaUiCommands.SHOW);
							} else {
								console.info('Has MFA history - skipping success screen');
								this.mfaService.ss_setMfaActivatedAt(MfaSetupState.ALREADY_SET);
							}

							resolve(showSuccessScreen);
						},
						error: error => {
							console.error('Error fetching MFA activated status:', error);
							resolve(false);
						},
					});
				});
			} else {
				// Just fetch and log (fire and forget)
				profileService.getMfaActivatedStatus(email).subscribe({
					next: response => {
						console.info('MFA activated status fetched successfully:', response);
					},
					error: error => {
						console.error('Error fetching MFA activated status:', error);
					},
				});
			}
		} catch (error) {
			console.error('Error in fetchMfaActivatedStatusAfterTokenSet:', error);
			if (checkForSuccessScreen) {
				return Promise.resolve(false);
			}
		}
	}

	/**
	 * Show MFA Success screen if needed and return an observer to handle it's dismiss OTHERWISE return false
	 * @returns `false` if user has already seen MFA success screen, (based on their MFA Setup State, `FIRST_TIME` or `ALREADY_SET`) \
	 * Otherwise an `Observable` which sends `true` when MFA Success screen is closed. \
	 * Observer is auto completed after received once.
	 */
	private isShowingMfaSuccessScreen() {
		// this.spinner.stopLoader();
		const mfaActivatedAt = this.mfaService.ss_getMfaActivatedAt();
		console.info('MFA -> ss_getMfaActivatedAt:', mfaActivatedAt);

		if (!mfaActivatedAt || mfaActivatedAt === MfaSetupState.UNDEFINED) {
			console.info('MFA -> MFA Success Screen - Showing');
			this.spinner.stopLoader();
			this.mfaService.setMfaActivatedAt(MfaSetupState.FIRST_TIME);
			this.mfaService.ss_setMfaActivatedAt(MfaSetupState.FIRST_TIME);

			const subject = new Subject<true>();

			const subscription = this.mfaService.displayMfaSuccessScreen.subscribe(uiCommand => {
				if (uiCommand === MfaUiCommands.HIDE) {
					console.info('MFA -> MFA Success Screen - Dismissed');
					subscription.unsubscribe();
					this.spinner.startLoader();
					this.mfaService.toggleMfaSuccessScreen(MfaUiCommands.HIDE);
					subject.next(true);
					subject.complete();
				}
			});
			this.loginFlowSubscriptions.add(subscription);
			return subject.asObservable();
		} else {
			return false;
		}
	}

	private async completeMFAChallenge(code: string): Promise<ConfirmSignInOutputWithError> {
		try {
			this.spinner.startLoader();
			const response = await confirmSignIn({ challengeResponse: code });
			return response; // Return success if no error occurs
		} catch (error) {
			console.log('confirmSignIn Error:', error);
			this.spinner.stopLoader();
			if (error?.name) {
				console.log('confirmSignIn Error:', JSON.stringify(error));
				if (error.name === 'NotAuthorizedException') {
					this.handleLoginSessionExpiredForMfa();
				} else if (error.name === 'CodeMismatchException') {
					this.mfaService.displayMfaErrorMessage(
						true,
						'The code you entered is incorrect. Please re-enter the code to try again.'
					);
				} else if (error.name === 'ExpiredCodeException') {
					this.mfaService.displayMfaErrorMessage(true, 'MFA code has expired');
				} else if (error.name === 'LimitExceededException') {
					this.mfaService.displayMfaErrorMessage(true, 'Limit Exceeded');
				}
			} else {
				console.error('An unexpected error occurred during MFA verification:', error);
				this.mfaService.displayMfaErrorMessage(true, 'An unexpected error occurred during MFA verification');
			}
			return { isSignedIn: false, nextStep: undefined, error };
		}
	}

	private clearLoginSessionTimeout(): void {
		if (this.loginSessionTimeout) {
			clearInterval(this.loginSessionTimeout);
			console.info(
				'MFA INPUT -> Login Session - Cleared:',
				this.loginSessionTimeout,
				`${new Date().getHours()}:${new Date().getMinutes()}:${new Date().getSeconds()}`
			);
			this.loginSessionTimeout = undefined;
		}
	}

	private handleLoginSessionExpiredForMfa(): void {
		console.info(
			'MFA INPUT -> Login Session Expired:',
			`${new Date().getHours()}:${new Date().getMinutes()}:${new Date().getSeconds()}`
		);
		this.modalService.openSessionExpireModal(
			'LOGIN SESSION EXPIRED',
			`Your login session has expired, please try again and submit OTP within 3 minutes!`,
			() => {
				this.mfaService.toggleDisplayMfaInputScreen(MfaUiCommands.HIDE);
				const queryParams = { queryParams: { tab: 'login' } };
				this.router.navigate(['/login'], queryParams);
			}
		);
	}

	getCurrentAmplifyUserAuthSession(): Promise<AuthSession> {
		const isSSO = sessionStorage.getItem('sso') === 'true';
		const ssoType = sessionStorage.getItem('sso-type');
		const ssoJwtToken = this.jwtHelper.getDecodedToken();

		if (this.currentUserSession) {
			const idTokenStr = this.currentUserSession?.tokens?.idToken?.toString();
			if (!idTokenStr) {
				this.currentUserSession = undefined;
				this.currentUserSessionPromise = undefined;
				return this.getCurrentAmplifyUserAuthSession();
			} else if (this.isTokenExpired(idTokenStr)) {
				this.handleTokenSessionError();
			} else {
				return Promise.resolve(this.currentUserSession);
			}
		} else if (this.currentUserSessionPromise) {
			return this.currentUserSessionPromise;
		} else if (isSSO && ssoJwtToken) {
			if (ssoType === SsoType.IDPInitiated) {
				const { idToken, jwtToken } = JSON.parse(sessionStorage.getItem('sso-tokens')) || {};
				if (idToken && jwtToken) {
					const authSession = SsoIdpInitiatedComponent.getAuthSession(idToken, jwtToken);
					this.currentUserSessionPromise = this.updateAwsCredentialsForSsoIdp(authSession);
				}
			} else if (ssoType === SsoType.SPInitiated) {
				const { accessToken, idToken } = JSON.parse(sessionStorage.getItem('sso-tokens')) || {};
				if (accessToken && idToken) {
					const authSession = SsoRedirectionComponent.getAuthSession(accessToken, idToken);
					this.currentUserSessionPromise = this.updateAwsCredentialsForSsoSp(authSession);
				}
			}
		}
		if (!this.currentUserSessionPromise) {
			this.currentUserSessionPromise = fetchAuthSession();
		}
		this.currentUserSessionPromise.then(authSession => {
			if (!this.currentUserSession) {
				this.currentUserSession = authSession;
			}
		});
		return this.currentUserSessionPromise;
	}

	/**
	 * Update AWS credentials of current user
	 */
	updateAwsCredentials(authSession?: AuthSession) {
		AWS_SDK.config.region = 'us-east-1';

		const doUpdate = (authSessionArg: AuthSession) => {
			if (!this.currentUserSession && !this.currentUserSessionPromise) {
				const Logins = {};
				Logins[this.env.LOGINS_URL] = authSessionArg?.tokens?.idToken?.toString();
				AWS_SDK.config.update({
					credentials: new AWS_SDK.CognitoIdentityCredentials({
						IdentityPoolId: PoolData.IdentityPoolId,
						IdentityId: authSession.identityId,
						Logins: Logins,
					}),
					token: new AWS_SDK.Token({ token: authSessionArg?.tokens?.idToken?.toString() }),
				});
			}
		};

		if (authSession) {
			if (authSession) {
				doUpdate(authSession);
			}
		} else {
			this.getCurrentAmplifyUserAuthSession().then(
				authSessionRes => {
					if (authSessionRes) {
						doUpdate(authSessionRes);
					}
				},
				error => {
					console.error('updateAwsCredentials().getCurrentAmplifyUserAuthSession():', error);
				}
			);
		}
	}

	/**
	 * Update AWS credentials of current SSO user
	 */
	async updateAwsCredentialsForSsoIdp(authSession: AuthSession) {
		AWS_SDK.config.region = 'us-east-1';

		const Logins = {};
		Logins[this.env.LOGINS_URL] = authSession?.tokens?.idToken?.toString();
		AWS_SDK.config.update({
			credentials: new AWS_SDK.CognitoIdentityCredentials({
				IdentityPoolId: PoolData.IdentityPoolId,
				IdentityId: authSession.identityId,
				Logins: Logins,
			}),
			token: new AWS_SDK.Token({ token: authSession?.tokens?.idToken?.toString() }),
		});

		if (this.currentUserSession) {
			this.currentUserSession = Object.assign(this.currentUserSession, authSession);
		} else {
			const anonAuthSession = await fetchAuthSession();
			this.currentUserSession = Object.assign(anonAuthSession, authSession);
		}
		return this.currentUserSession;
	}

	/**
	 * Update AWS credentials of current SSO user
	 */
	async updateAwsCredentialsForSsoSp(authSession: AuthSession) {
		AWS_SDK.config.region = 'us-east-1';

		if (authSession?.tokens?.idToken && authSession?.tokens?.accessToken) {
			const Logins: LoginsMap = { [this.env.LOGINS_URL]: authSession.tokens.idToken.toString() };

			AWS_SDK.config.update({
				credentials: new AWS_SDK.CognitoIdentityCredentials({
					IdentityPoolId: PoolData.IdentityPoolId,
					Logins,
				}),
				token: new AWS_SDK.Token({ token: authSession.tokens.idToken.toString() }),
			});

			while (true) {
				const awsCredentials = (await this.getAwsCredentials()) as AWS_SDK.CognitoIdentityCredentials;

				AWS_SDK.config.update({
					credentials: awsCredentials,
					token: new AWS_SDK.Token({ token: authSession.tokens.idToken.toString() }),
				});

				if (awsCredentials?.accessKeyId) {
					this.currentUserSession = {
						credentials: {
							accessKeyId: awsCredentials.accessKeyId,
							secretAccessKey: awsCredentials.secretAccessKey,
							expiration: awsCredentials.expireTime,
							sessionToken: awsCredentials.sessionToken,
						},
						identityId: awsCredentials.identityId,
						tokens: { accessToken: authSession.tokens.accessToken, idToken: authSession.tokens.idToken },
						userSub: authSession.tokens.idToken.payload.sub,
					};

					const cognitoUserSession = new CognitoUserSession({
						AccessToken: new CognitoAccessToken({ AccessToken: authSession.tokens.accessToken?.toString() }),
						IdToken: new CognitoIdToken({ IdToken: authSession.tokens.idToken?.toString() }),
						RefreshToken: new CognitoRefreshToken({ RefreshToken: undefined }),
					});

					this.cognitoUser = new CognitoUser({
						Username: this.currentUserSession.userSub,
						Pool: userPool,
					});
					this.cognitoUser.setSignInUserSession(cognitoUserSession);

					/* --- OTHER METHODS - START ---------------------------------------------------------------
						Keeping other methods below just in case above stops working for some reason
					*/
					// const accessTokenIssuedAtInMMilliseconds = (authSession.tokens.accessToken.payload.iat || 0) * 1000;
					// const currentTime = new Date().getTime();
					// const tokens: CognitoAuthTokens = {
					// 	accessToken: authSession.tokens.accessToken,
					// 	idToken: authSession.tokens.idToken,
					// 	clockDrift: accessTokenIssuedAtInMMilliseconds > 0 ? accessTokenIssuedAtInMMilliseconds - currentTime : 0,
					// 	username: this.currentUserSession.userSub,
					// };

					// const authTokenStore = new DefaultTokenStore();
					// authTokenStore.setAuthConfig(Amplify.getConfig().Auth);
					// authTokenStore.setKeyValueStorage(defaultStorage);
					// const tokenOrchestrator = new TokenOrchestrator();
					// tokenOrchestrator.setAuthConfig(Amplify.getConfig().Auth)
					// tokenOrchestrator.setAuthTokenStore(authTokenStore);
					// tokenOrchestrator.setTokens({ tokens });

					// Another Method
					// defaultStorage.setItem(`CognitoIdentityServiceProvider.${PoolData.ClientId}.LastAuthUser`, idTokenPayload['cognito:username'].toString());
					// defaultStorage.setItem(`CognitoIdentityServiceProvider.${PoolData.ClientId}.${idTokenPayload['cognito:username']}.idToken`, authSession.tokens.idToken.toString());
					// defaultStorage.setItem(`CognitoIdentityServiceProvider.${PoolData.ClientId}.${idTokenPayload['cognito:username']}.accessToken`, authSession.tokens.accessToken.toString());
					// defaultStorage.setItem(`CognitoIdentityServiceProvider.${PoolData.ClientId}.${idTokenPayload['cognito:username']}.refreshToken`, '');
					// defaultStorage.setItem(`CognitoIdentityServiceProvider.${PoolData.ClientId}.${idTokenPayload['cognito:username']}.clockDrift`, `${calculateClockDrift(accessTokenData['iat'], idTokenData['iat'])}`);
					/* --- OTHER METHODS - END ----------------------------------------------------------------- */
					break;
				}
			}
			return this.currentUserSession;
		}
	}

	handleTokenSessionError() {
		this.modalService.openSessionExpireModal(
			'SESSION TOKEN EXPIRED',
			`Your session token has expired. Please try logging in a new tab if you are using SSO.`
		);
		this.logoutUser();
		console.error(Error('Session Token Expired'));
	}

	isLoggedIn() {
		return userPool.getCurrentUser();
	}

	getUnauthCredentials(): Promise<AWS_SDK.Credentials | CredentialsOptions> {
		return new Promise(resolve => {
			const _self = this;
			AWS_SDK.config.update({
				region: _self.config.cognitoConfig.region,
				credentials: new AWS_SDK.CognitoIdentityCredentials({
					IdentityPoolId: PoolData.IdentityPoolId,
				}),
			});
			resolve(AWS_SDK.config.credentials);
		});
	}

	updateAwsUnauthCredentials() {
		const _self = this;
		AWS_SDK.config.update({
			region: _self.config.cognitoConfig.region,
			credentials: new AWS_SDK.CognitoIdentityCredentials({
				IdentityPoolId: PoolData.IdentityPoolId,
			}),
		});
	}

	/**
	 * Logs out current user
	 * clears local storage and session storage
	 */
	logoutUser() {
		if (typeof document !== 'undefined') {
			document.body.style.setProperty('--sidebar-width', '0px');
		}
		if (userPool.getCurrentUser() !== null) {
			userPool.getCurrentUser().signOut();
		}
		if (AWS_SDK.config.credentials && !sessionStorage.getItem('sso-type')) {
			AWS.config.credentials.clearCachedId();
			AWS_SDK.config.credentials = new AWS_SDK.CognitoIdentityCredentials({
				IdentityPoolId: PoolData.IdentityPoolId,
			});
		}
		if (window[this.config.userLoginSession.sessionCheckIntervalVarName]) {
			clearInterval(window[this.config.userLoginSession.sessionCheckIntervalVarName]);
		}
		this.currentUserSession = undefined;
		this.currentUserSessionPromise = undefined;
		signOut();
		const sidebarCollapsed = localStorage.getItem('sidebarCollapsed');
		localStorage.clear();
		sessionStorage.clear();
		if (sidebarCollapsed) {
			localStorage.setItem('sidebarCollapsed', sidebarCollapsed);
		}
		this.dataCarrier.reset();
		this.mfaService.fetchGlobalMfaStatus();
	}

	/**
	 * Cognito forgot password
	 * @param email
	 */
	forgotPassword(email, sendLink: boolean = false, returnUrl: string = '') {
		const userData = {
			Username: email,
			Pool: userPool,
		};
		const cognitoUser = new CognitoUser(userData);
		this.result = new Subject<any>();
		let clientMetadata = {};
		const utmParams = this.getUtmParameters();
		if (sendLink && utmParams) {
			clientMetadata = {
				sendLink: sendLink.toString(),
				returnUrl: returnUrl,
				...utmParams,
			};
		} else {
			clientMetadata = { sendLink: sendLink.toString(), returnUrl: returnUrl };
		}
		cognitoUser.forgotPassword(
			{
				onSuccess: result => {
					const res = { success: true, object: result };
					this.result.next(res);
				},
				onFailure: err => {
					console.error(`[${AuthService.name}][${this.forgotPassword.name}]`, 'Error on forgot password', email, err);
					const error = { success: false, object: err };
					this.result.error(error);
				},
			},
			clientMetadata
		);
	}

	/**
	 * Cognito reset password
	 * @param email
	 */
	resetPassword(email, verificationCode, newPassword) {
		const userData = {
			Username: email,
			Pool: userPool,
		};
		const cognitoUser = new CognitoUser(userData);
		this.result = new Subject<any>();
		cognitoUser.confirmPassword(verificationCode, newPassword, {
			onSuccess: () => {
				const error = { success: true };
				this.result.next(error);
			},
			onFailure: (err: Error) => {
				console.error(`[${AuthService.name}][${this.resetPassword.name}]`, 'Error on reset password', email, err);
				const error = { success: false, object: err };
				this.result.error(error);
			},
		});
	}

	getResult(): Observable<any> {
		return this.result.asObservable().pipe(
			tap(res => {
				if (res.error) {
					throw res.error;
				}
			})
		);
	}

	getSignUpResult(): Observable<any> {
		return this.signUpResult.asObservable();
	}

	/**
	 * Cognito update user password
	 * @param email
	 */
	updateUserPassword(oldPassword, newPassword) {
		this.result = new Subject<any>();
		const cognitoUser = userPool.getCurrentUser();
		if (cognitoUser == null) {
			this.result.error({ success: false, object: new Error('No active Cognito user') });
			return;
		}
		cognitoUser.getSession((err, session) => {
			if (err) {
				console.error(`[${AuthService.name}][${this.updateUserPassword.name}][1]`, 'Error getting session', err);
				this.result.error({ success: false, object: err });
				return;
			}
			cognitoUser.changePassword(oldPassword, newPassword, (changeErr, res) => {
				if (changeErr) {
					console.error(
						`[${AuthService.name}][${this.updateUserPassword.name}][2]`,
						'Error updating user password',
						changeErr
					);
					this.result.error({ success: false, object: changeErr });
				} else {
					this.result.next({ success: true, object: '' });
				}
			});
		});
	}

	getAwsCredentials(): Promise<AWS_SDK.Credentials | CredentialsOptions | AWS_SDK.CognitoIdentityCredentials | null> {
		return new Promise((resolve, reject) => {
			AWS_SDK.config.getCredentials((error, credentials) => {
				if (error) {
					reject(error);
				} else {
					resolve(credentials);
				}
			});
		});
	}

	/**
	 * Set AWS Credential for auto login using IdToken getting from SSO
	 * @param authToken - Token from SSO
	 */
	setAWSCredentials(authToken: any) {
		AWS_SDK.config.region = this.config.cognitoConfig.region;
		const Logins = {};
		Logins[this.environment.getEnvironment().LOGINS_URL] = authToken.idToken.jwtToken;
		AWS_SDK.config.credentials = new AWS_SDK.CognitoIdentityCredentials({
			IdentityPoolId: PoolData.IdentityPoolId,
			Logins,
		});
	}

	/**
	 * Sets utm params in local storage
	 * @param params Utm Params
	 */
	setUtmParameters(params: any) {
		const utm = new UTMParameters();
		if (
			params.utm_source ||
			params.utm_medium ||
			params.utm_term ||
			params.utm_content ||
			params.utm_campaign ||
			params.clientToken
		) {
			utm.utmSource = params.utm_source;
			utm.utmMedium = params.utm_medium;
			utm.utmTerm = params.utm_term;
			utm.utmContent = params.utm_content;
			utm.utmCampaign = params.utm_campaign;
			utm.clientToken = params.clientToken;
			localStorage.setItem('utm', JSON.stringify(utm));
		}
	}

	/**
	 * Gets Utm params object from local storage
	 */
	getUtmParameters() {
		if (localStorage.getItem('utm')) {
			const params = JSON.parse(localStorage.getItem('utm'));
			if (params) {
				const utmParams = new UtmParams();
				utmParams.utm_campaign = params.utmCampaign;
				utmParams.utm_content = params.utmContent;
				utmParams.utm_medium = params.utmMedium;
				utmParams.utm_source = params.utmSource;
				utmParams.utm_term = params.utmTerm;
				return utmParams;
			}
		}
	}
}
